import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  DeploymentListParams,
  DeploymentOverviewParams,
  DeploymentPlanParams,
  deploymentApi
} from '@/api/deploymentApi'
import { getPageByUrl } from '@/api/vpApi'
import { LibraryLogic } from '@/types/businessLogic'
import { DeploymentConfig } from '@/types/deployment'
import { DeploymentOverview } from '@/types/deploymentGroup'
import { PaginatedResponse } from '@/types/pagination'
import { objectToQueryString } from '@/utils/url'

export const deploymentQueryKeys = {
  all: ['deployments'] as const,
  list: (params?: DeploymentListParams) =>
    [...deploymentQueryKeys.all, 'list', params] as const,
  overview: (params?: DeploymentOverviewParams) =>
    [...deploymentQueryKeys.all, 'list', 'overview', params] as const,
  deviceGroups: (deviceId: string) =>
    [
      ...deploymentQueryKeys.all,
      'list',
      'overview',
      'device-groups',
      deviceId
    ] as const,
  libraryLogics: () => [...deploymentQueryKeys.all, 'libraryLogics'] as const,
  deviceFilterOptions: () =>
    [...deploymentQueryKeys.all, 'deviceFilterOptions'] as const,
  plan: (data: DeploymentConfig, params?: DeploymentPlanParams) =>
    [...deploymentQueryKeys.all, 'plan', params, data] as const
}

export const useGetLibraryLogics = () => {
  return useInfiniteQuery({
    queryKey: deploymentQueryKeys.libraryLogics(),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<LibraryLogic[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam: `/v1/deployments/business-logics/?${objectToQueryString({
      limit: 100
    })}`
  })
}

export const useGetDeploymentList = (
  params: DeploymentListParams,
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) => {
  return useQuery({
    queryKey: deploymentQueryKeys.list(params),
    queryFn: () => deploymentApi.getDeploymentList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled
  })
}

export const useGetDeploymentPlan = (
  data: DeploymentConfig,
  params: DeploymentPlanParams,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: deploymentQueryKeys.plan(data, params),
    queryFn: () => deploymentApi.planDeployment(data, params),
    enabled: options?.enabled,
    retry: false,
    staleTime: 0
  })
}

export const useGetDeploymentOverview = (
  params: DeploymentOverviewParams,
  options?: {
    keepPreviousData?: boolean
    enabled?: boolean
    refetchInterval?: number
  }
) => {
  return useQuery({
    queryKey: deploymentQueryKeys.overview(params),
    queryFn: () => deploymentApi.getDeploymentOverview(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval
  })
}

const OVERVIEW_PAGE_LIMIT = 100

const getEveryOverviewPage = async (params: DeploymentOverviewParams) => {
  const pages = [await deploymentApi.getDeploymentOverview(params)]

  while (pages[pages.length - 1].next) {
    pages.push(
      await getPageByUrl<DeploymentOverview>(pages[pages.length - 1].next!)
    )
  }

  return pages.flatMap((page) => page.results)
}

export const useGetDeviceGroups = (deviceId: string, enabled = true) => {
  return useQuery({
    queryKey: deploymentQueryKeys.deviceGroups(deviceId),
    queryFn: async () =>
      (
        await getEveryOverviewPage({
          device_id: deviceId,
          limit: OVERVIEW_PAGE_LIMIT
        })
      )
        .flatMap((app) => app.devices)
        .filter((overviewDevice) => overviewDevice.device_id === deviceId)
        .flatMap((overviewDevice) => overviewDevice.groups),
    enabled
  })
}

export const useInvalidateDeploymentList = () => {
  const queryClient = useQueryClient()

  const invalidateDeploymentList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...deploymentQueryKeys.all, 'list']
    })
  }, [queryClient])

  return {
    invalidateDeploymentList
  }
}

export const useCreateDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.createDeployment
  })
}

export const useUpdateDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.updateDeployment
  })
}

export const useStopDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.stopDeployment
  })
}

export const useStartDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.startDeployment
  })
}

export const useRemoveAppDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.removeAppDeployment
  })
}

export const useRemoveCameraDeployment = () => {
  return useMutation({
    mutationFn: deploymentApi.removeCameraDeployment
  })
}
